import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { subadminService } from '../services/SubadminServices';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const [info, setInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [netEarnings, setNetEarnings] = useState(0);
  const [recentTransactions, setRecentTransactions] = useState({ data: [], total: 0, page: 1, limit: 10 });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const userId = localStorage.getItem('id');

  const fetchAdminInfo = async () => {
    try {
      setLoading(true);
      const response = await subadminService.getAdminInfo(userId);
      if (response.status === 200) {
        setInfo(response.data.data);
        setStats(response.data.stats);
        setNetEarnings(response.data.netEarnings);
        setRecentTransactions(response.data.recentTransactions);
      }
    } catch (error) {
      console.error("Error fetching admin info:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchAdminInfo();
    }
  }, [userId]);

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Prepare chart data
  const walletData = [
    { name: 'Credit', value: stats?.wallet?.totalCredit || 0 },
    { name: 'Debit', value: stats?.wallet?.totalDebit || 0 }
  ];

  const topupStatusData = stats?.topups?.byStatus ? [
    { name: 'Pending', value: stats.topups.byStatus.pending },
    { name: 'Approved', value: stats.topups.byStatus.approved },
    { name: 'Cancelled', value: stats.topups.byStatus.cancelled }
  ] : [];

  const statsOverview = [
    { name: 'Users', value: stats?.users || 0 },
    { name: 'Withdrawals', value: stats?.withdrawals || 0 },
    { name: 'Plans', value: stats?.plans?.purchased || 0 },
    { name: 'Referrals', value: stats?.referrals?.total || 0 },
    { name: 'Support Tickets', value: stats?.supportTickets?.pending || 0 }
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // In real implementation, you would fetch data for the specific page
    // For now, we'll just update the current page state
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderPagination = () => {
    const totalPages = Math.ceil(recentTransactions.total / recentTransactions.limit);
    const pages = [];
    
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <button className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </button>
        </li>
      );
    }
    
    return (
      <nav aria-label="Page navigation">
        <ul className="pagination justify-content-center">
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
              Previous
            </button>
          </li>
          {pages}
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
              Next
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  if (loading) {
    return (
      <div className="main px-lg-4 px-md-4">
        <div className="body d-flex py-3">
          <div className="container-xxl">
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main px-lg-4 px-md-4">
      <div className="body d-flex py-3">
        <div className="container-xxl">
          {/* Profile Section */}
          <div className="row g-3 mb-3">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <div className="row g-3 align-items-center">
                    <div className="col-md-6 col-lg-6 col-xl-3">
                      <div className="d-flex">
                        <img className="avatar rounded-circle" src="../assets/images/profile_av.svg" alt="profile" />
                        <div className="flex-fill ms-3">
                          <p className="mb-0">
                            <span className="font-weight-bold">{info?.fullName}</span>
                            <span className="badge bg-primary ms-2">{info?.role}</span>
                          </p>
                          <small className="">{info?.email}</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-6 col-xl-9">
                      <div className="row">
                        <div className="col-6 col-lg-3">
                          <div className="text-center">
                            <h4 className="mb-0 text-primary">{formatCurrency(netEarnings)}</h4>
                            <small className="text-muted">Net Earnings</small>
                          </div>
                        </div>
                        <div className="col-6 col-lg-3">
                          <div className="text-center">
                            <h4 className="mb-0 text-success">{stats?.users || 0}</h4>
                            <small className="text-muted">Total Users</small>
                          </div>
                        </div>
                        <div className="col-6 col-lg-3">
                          <div className="text-center">
                            <h4 className="mb-0 text-warning">{stats?.plans?.purchased || 0}</h4>
                            <small className="text-muted">Plans Sold</small>
                          </div>
                        </div>
                        <div className="col-6 col-lg-3">
                          <div className="text-center">
                            <h4 className="mb-0 text-info">{stats?.topups?.total || 0}</h4>
                            <small className="text-muted">Total Topups</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="row g-3 mb-3">
            <div className="col-xl-8">
              <div className="card">
                <div className="card-header py-3 d-flex justify-content-between bg-transparent border-bottom">
                  <h6 className="mb-0 fw-bold">Platform Overview</h6>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={statsOverview}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#0088FE" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="col-xl-4">
              <div className="card">
                <div className="card-header py-3 d-flex justify-content-between bg-transparent border-bottom">
                  <h6 className="mb-0 fw-bold">Wallet Distribution</h6>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={walletData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {walletData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="row g-3 mb-3">
            <div className="col-xl-4">
              <div className="card">
                <div className="card-header py-3 d-flex justify-content-between bg-transparent border-bottom">
                  <h6 className="mb-0 fw-bold">Topup Status</h6>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={topupStatusData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {topupStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="col-xl-4">
              <div className="card">
                <div className="card-header py-3 d-flex justify-content-between bg-transparent border-bottom">
                  <h6 className="mb-0 fw-bold">Plans Overview</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3 text-center">
                    <div className="col-6">
                      <div className="border-end">
                        <h4 className="mb-0 text-success">{stats?.plans?.active || 0}</h4>
                        <small className="text-muted">Active Plans</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <h4 className="mb-0 text-primary">{stats?.plans?.purchased || 0}</h4>
                      <small className="text-muted">Total Purchased</small>
                    </div>
                    <div className="col-12 mt-3">
                      <div className="bg-light p-3 rounded">
                        <h5 className="mb-1">{formatCurrency(stats?.plans?.totalInvested || 0)}</h5>
                        <small className="text-muted">Total Invested</small>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="bg-light p-3 rounded">
                        <h5 className="mb-1">{stats?.plans?.totalLockTime || 0} days</h5>
                        <small className="text-muted">Total Lock Time</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-4">
              <div className="card">
                <div className="card-header py-3 d-flex justify-content-between bg-transparent border-bottom">
                  <h6 className="mb-0 fw-bold">Top Referrers</h6>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Referrals</th>
                          <th>Earnings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats?.referrals?.leaderboard?.slice(0, 3).map((referrer, index) => (
                          <tr key={index}>
                            <td>
                              <div>
                                <div className="fw-bold">{referrer.user_id.fullName}</div>
                                <small className="text-muted">{referrer.user_id.email}</small>
                              </div>
                            </td>
                            <td>{referrer.total_referrals}</td>
                            <td>{formatCurrency(referrer.total_earnings)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Transactions */}
          <div className="row g-3 mb-3">
            <div className="col-xl-12">
              <div className="card">
                <div className="card-header py-3 d-flex justify-content-between">
                  <h6 className="mb-0 fw-bold">Recent Transactions</h6>
                  <small className="text-muted">Total: {recentTransactions.total}</small>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>User</th>
                          <th>Type</th>
                          <th>Amount</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentTransactions.data.map((transaction, index) => (
                          <tr key={transaction._id}>
                            <td>
                              <small>{formatDate(transaction.created_at)}</small>
                            </td>
                            <td>
                              <div>
                                <div className="fw-bold">{transaction.user_id.fullName}</div>
                                <small className="text-muted">{transaction.user_id.email}</small>
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${transaction.transaction_type === 'credit' ? 'bg-success' : 'bg-danger'}`}>
                                {transaction.transaction_type}
                              </span>
                            </td>
                            <td>
                              <span className={transaction.transaction_type === 'credit' ? 'text-success' : 'text-danger'}>
                                {transaction.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(transaction.amount)}
                              </span>
                            </td>
                            <td>
                              <small>{transaction.description}</small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  {recentTransactions.total > recentTransactions.limit && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        <small className="text-muted">
                          Showing {((currentPage - 1) * recentTransactions.limit) + 1} to {Math.min(currentPage * recentTransactions.limit, recentTransactions.total)} of {recentTransactions.total} entries
                        </small>
                      </div>
                      {renderPagination()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Support Tickets & Quick Actions */}
          <div className="row g-3 mb-3">
            <div className="col-xl-6">
              <div className="card">
                <div className="card-header py-3 d-flex justify-content-between bg-transparent border-bottom">
                  <h6 className="mb-0 fw-bold">Support Tickets</h6>
                </div>
                <div className="card-body text-center">
                  <div className="row">
                    <div className="col-6">
                      <div className="border-end">
                        <h3 className="mb-0 text-warning">{stats?.supportTickets?.pending || 0}</h3>
                        <small className="text-muted">Pending</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <h3 className="mb-0 text-success">0</h3>
                      <small className="text-muted">Resolved</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-6">
              <div className="card">
                <div className="card-header py-3 d-flex justify-content-between bg-transparent border-bottom">
                  <h6 className="mb-0 fw-bold">Quick Actions</h6>
                </div>
                <div className="card-body">
                  <div className="d-grid gap-2">
                    <Link to="/admin/users" className="btn btn-primary">Manage Users</Link>
                    <Link to="/admin/plans" className="btn btn-success" >Manage Plans</Link>
                    <Link to="/admin/withdrawals" className="btn btn-warning">Review Withdrawals</Link>
                    <Link to="/admin/support-tickets" className="btn btn-info">Support Tickets</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;